import { tracker } from '@lessonup/analytics';
import { useDocumentQuery } from '@lessonup/client-integration';
import { decodeYjsPins } from '@lessonup/editor-shared';
import { defaultPinTheme, getStripDownPin, ThemeSettingsSelection, UpdatePinDispatch } from '@lessonup/pin-renderer';
import { Pin, PinTheme } from '@lessonup/pins-shared';
import {
  color,
  createModal,
  ModalPopover,
  ModalPopoverAnchor,
  ModalPopoverDirection,
  styled,
  useModal,
} from '@lessonup/ui-components';
import { compact } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useBoolean } from 'usehooks-ts';
import { Doc } from 'yjs';
import { GetPinThemesDocument } from '../../../Editor.graphql.generated';
import { ThemeDetailView } from './ThemeDetailView';
import { ThemeListView } from './ThemeListView';
import { ThemeViewMode } from './types';
import { getInitialThemeSettingsSelection } from './utils';

interface ModalProps {
  direction: ModalPopoverDirection;
  modalAnchor: ModalPopoverAnchor;
  parentSelector: () => HTMLLIElement | HTMLButtonElement | null;
  pin: Pin;
  selectedPins: Pin[];
  currentPinTheme: PinTheme;
  dispatch: UpdatePinDispatch;
  yDoc: Doc;
  lessonId: string;
  allPins: Pin[];
}

const PIN_WIDTH = 236;
const DETAIL_PIN_WIDTH = 480;

export const SwitchPinThemeModal = createModal(
  ({
    direction,
    modalAnchor,
    parentSelector,
    pin,
    selectedPins,
    currentPinTheme,
    dispatch,
    yDoc,
    lessonId,
    allPins,
  }: ModalProps) => {
    const { data, loading } = useDocumentQuery(GetPinThemesDocument, { fetchPolicy: 'cache-first' });
    const modal = useModal();
    const decodedPin = useMemo(() => decodeYjsPins(yDoc, [pin])[0], [pin, yDoc]);
    const strippedDownPin = useMemo(() => getStripDownPin(decodedPin), [decodedPin]);
    const allDecodedPins = useMemo(() => decodeYjsPins(yDoc, allPins), [allPins, yDoc]);

    const [viewMode, setViewMode] = useState<ThemeViewMode>('list');
    const [selectedTheme, setSelectedTheme] = useState<PinTheme | null>(null);
    const [themeSettings, setThemeSettings] = useState<ThemeSettingsSelection>(() =>
      getInitialThemeSettingsSelection(decodedPin)
    );
    const applyToAllPins = useBoolean(false);

    const handleThemeSelect = (theme: PinTheme | null) => {
      setSelectedTheme(theme);
      const pinsForSelection = applyToAllPins.value ? allDecodedPins : [decodedPin];
      setThemeSettings(getInitialThemeSettingsSelection(decodedPin, pinsForSelection));
      setViewMode('detail');
    };

    const handleApplyToAllToggle = (value: boolean) => {
      if (value) {
        applyToAllPins.setTrue();
        setThemeSettings(getInitialThemeSettingsSelection(decodedPin, allDecodedPins));
      } else {
        applyToAllPins.setFalse();
        setThemeSettings(getInitialThemeSettingsSelection(decodedPin, [decodedPin]));
      }
    };

    const handleBackToList = () => {
      setViewMode('list');
      setSelectedTheme(null);
    };

    const handleApplyTheme = () => {
      const selectedPinIds = selectedPins.map((pin) => pin.id);
      const allPinsToApply = applyToAllPins.value;

      const shouldResetAnySettings =
        themeSettings.pinSettings.background || Object.values(themeSettings.componentSettings).some((v) => v);

      dispatch(
        compact([
          selectedTheme
            ? {
                type: 'addTheme',
                pinTheme: selectedTheme,
                allPins: allPinsToApply,
                pinIds: selectedPinIds,
              }
            : {
                type: 'removeTheme',
                allPins: allPinsToApply,
                pinIds: selectedPinIds,
              },
          shouldResetAnySettings
            ? {
                type: 'resetPinsGranularly',
                pinIds: selectedPinIds,
                allPins: allPinsToApply,
                selections: themeSettings,
              }
            : undefined,
        ])
      );

      tracker.events.themeApplied({
        lessonId,
        themeId: selectedTheme?.themeId || '',
        themeSettingResetOverwrites: shouldResetAnySettings,
        themeSettingApplyToAll: allPinsToApply,
        themeVersion: selectedTheme?.id || '',
        pinCount: allPinsToApply ? allPins.length : selectedPinIds.length,
      });

      modal.hide();
    };

    const detailTheme = selectedTheme ?? defaultPinTheme;

    return (
      <ModalPopover
        direction={direction}
        modalAnchor={modalAnchor}
        parentSelector={parentSelector}
        modal={modal}
        contentLabel="Switch Pin Theme"
      >
        {viewMode === 'list' ? (
          <StyledListModalContent>
            <ThemeListView
              loading={loading}
              themes={data?.pinThemes || []}
              currentPinTheme={currentPinTheme}
              strippedPin={strippedDownPin}
              pinWidth={PIN_WIDTH}
              onThemeSelect={handleThemeSelect}
            />
          </StyledListModalContent>
        ) : (
          <StyledDetailModalContent>
            <ThemeDetailView
              theme={detailTheme}
              pin={decodedPin}
              allPins={applyToAllPins.value ? allDecodedPins : [decodedPin]}
              pinWidth={DETAIL_PIN_WIDTH}
              selections={themeSettings}
              onSelectionChange={setThemeSettings}
              applyingToAllSlides={applyToAllPins.value}
              onApplyToAllSlidesChange={handleApplyToAllToggle}
              onBack={handleBackToList}
              onApply={handleApplyTheme}
            />
          </StyledDetailModalContent>
        )}
      </ModalPopover>
    );
  }
);

const StyledListModalContent = styled.div`
  background: ${color.neutral.surface1.background};
  width: 540px;
  max-height: 600px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const StyledDetailModalContent = styled.div`
  background: ${color.neutral.surface1.background};
  width: 600px;
  max-height: 650px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;
